import { BaseApiManager, ApiResponse } from './BaseApiManager';

export class AdminApiManager extends BaseApiManager {
  private static instance: AdminApiManager;

  private constructor() {
    super();
  }

  public static getInstance(): AdminApiManager {
    if (!AdminApiManager.instance) {
      AdminApiManager.instance = new AdminApiManager();
    }
    return AdminApiManager.instance;
  }

  // Dashboard endpoints
  public async getDashboard(): Promise<ApiResponse<any>> {
    return this.request('/core/admin/dashboard');
  }

  // User management endpoints
  public async getUsers(page: number = 1, limit: number = 20): Promise<ApiResponse<any>> {
    return this.request(`/core/admin/users?page=${page}&limit=${limit}`);
  }

  public async getUser(id: string | number): Promise<ApiResponse<any>> {
    return this.request(`/core/admin/users/${id}`);
  }

  public async updateUserLimits(
    id: string | number,
    data: { monthlyRequests?: number; dailyRequests?: number; maxSessions?: number }
  ): Promise<ApiResponse<any>> {
    return this.request(`/core/admin/users/${id}/limits`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  public async deleteUser(id: string | number): Promise<ApiResponse<any>> {
    return this.request(`/core/admin/users/${id}`, {
      method: 'DELETE',
    });
  }

  // Prompt management endpoints
  public async getPrompts(page: number = 1, limit: number = 20): Promise<ApiResponse<any>> {
    return this.request(`/core/admin/prompts?page=${page}&limit=${limit}`);
  }

  public async updatePrompt(
    id: string | number,
    data: { content?: string; version?: string; active?: boolean }
  ): Promise<ApiResponse<any>> {
    return this.request(`/core/admin/prompts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // AI Model configuration endpoints
  public async getModelConfig(): Promise<ApiResponse<any>> {
    return this.request('/core/admin/model-config');
  }

  public async updateModelGpt5(data: {
    temperature?: number;
    maxTokens?: number;
    topP?: number;
    frequencyPenalty?: number;
    presencePenalty?: number;
  }): Promise<ApiResponse<any>> {
    return this.request('/core/admin/model-config/gpt5', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  public async updateModelChat(data: {
    temperature?: number;
    maxTokens?: number;
    topP?: number;
    frequencyPenalty?: number;
    presencePenalty?: number;
  }): Promise<ApiResponse<any>> {
    return this.request('/core/admin/model-config/chat', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // TTS configuration endpoints
  public async getTtsConfig(): Promise<ApiResponse<any>> {
    return this.request('/core/admin/tts-config');
  }

  public async updateTtsConfig(
    id: string | number,
    data: { voice?: string; speed?: number; volume?: number; format?: string }
  ): Promise<ApiResponse<any>> {
    return this.request(`/core/admin/tts-config/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Error tracking endpoints
  public async getErrors(page: number = 1, limit: number = 50): Promise<ApiResponse<any>> {
    return this.request(`/core/admin/errors?page=${page}&limit=${limit}`);
  }

  public async clearErrors(): Promise<ApiResponse<any>> {
    return this.request('/core/admin/errors', {
      method: 'DELETE',
    });
  }

  // Analytics endpoints
  public async getSessionAnalytics(period?: string): Promise<ApiResponse<any>> {
    const query = period ? `?period=${period}` : '';
    return this.request(`/core/admin/analytics/sessions${query}`);
  }

  public async getUsageAnalytics(period?: string): Promise<ApiResponse<any>> {
    const query = period ? `?period=${period}` : '';
    return this.request(`/core/admin/analytics/usage${query}`);
  }

  // Intro script endpoints
  public async getIntroScript(): Promise<ApiResponse<any>> {
    return this.request('/core/admin/intro-script');
  }

  public async updateIntroScript(data: { scriptText?: string; active?: boolean }): Promise<ApiResponse<any>> {
    return this.request('/core/admin/intro-script', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Background audio endpoints
  public async getBackgroundAudio(): Promise<ApiResponse<any>> {
    return this.request('/core/admin/background-audio');
  }

  public async uploadBackgroundAudio(file: File): Promise<ApiResponse<any>> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = this.getToken();
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${this.baseUrl}/core/admin/background-audio/upload`, {
        method: 'POST',
        headers,
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          message: `HTTP ${response.status}: ${response.statusText}`,
        }));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const data = await response.json();
      return { data, success: true, status: response.status };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Upload failed';
      return { success: false, error: errorMessage };
    }
  }
}
